// Core packages
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  startWith
} from "rxjs/operators";

// Custom packages
import rawCountries from 'src/app/shared/mocks/countries.json';
import { GoogleapiService } from 'src/app/shared/services/google.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Component({
  selector: 'app-manual-address-dialog',
  templateUrl: './manual-address-dialog.component.html',
  styleUrls: ['./manual-address-dialog.component.scss']
})
export class ManualAddressDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  countries: any[] = [];
  // BEGIN - Manual address form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  // END - Manual address form

  required = false;

  dialogIsLoading = true;
  geocodingDisabled = true;
  latitude: any = undefined;
  longitude: any = undefined;

  constructor(
    public dialogRef: MatDialogRef<ManualAddressDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translateService: TranslateService,
    private helperService: HelperService,
    private googleapiService: GoogleapiService,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    if (typeof this.data.required !== 'undefined') {
      this.required = this.data.required;
    }

    // Set default icons if none are provided
    if (!this.data.btnOkIcon) {
      this.data.btnOkIcon = 'fa-check-circle';
    }
    if (!this.data.btnCancelIcon) {
      this.data.btnCancelIcon = 'fa-times-circle';
    }

    // Set default button colors if none are provided
    if (!this.data.btnOkColor) {
      this.data.btnOkColor = 'primary';
    }
    if (!this.data.btnCancelColor) {
      this.data.btnCancelColor = 'warn';
    }

    // Build countries array
    rawCountries.forEach(obj => {
      this.countries.push({
        value: obj.code,
        label: obj.name,
      });
    });

    // Init form
    this.initForm();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Handle click on Denial button
   *
   * @since 1.0.0
   */
  onDenial(): void {
    this.dialogRef.close();
  }

  /**
   * Handle click on confirm button
   *
   * @since 1.0.0
   */
  onConfirm(): void {
    if (this.form.invalid) {
      // Workaround to force mat-error display on untouched fields
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        if (control.invalid) {
          control.markAsTouched();
          control.markAsDirty();
        }
      });
      return;
    }

    this.dialogRef.close(this.model);
  }

  onGetGeocoding(): void {
    this.dialogIsLoading = true;
    this.subscriptions.push(
      this.googleapiService.getGeocoding(this.model).subscribe(
        res => {
          if (
            res && 
            Array.isArray(res) &&
            res.length > 0 &&
            res[0].geometry
          ) {
            this.form.get("latitude").setValue(res[0].geometry.location.lat);
            this.form.get("longitude").setValue(res[0].geometry.location.lng);
          }
          this.dialogIsLoading = false;
        },
        err => {
          this.dialogIsLoading = false;
          this.helperService.handleError(err);
        }
      )
    );
  }

  geocoding(): void {
    const lat = this.form.controls.latitude;
    const lng = this.form.controls.longitude;
    // console.log(lat, lng)
    if (lat && lng) {
      lat.valueChanges
        .pipe(distinctUntilChanged(), startWith(lat.value))
        .subscribe(
          (res) => {
            this.latitude = res;

            if (
              !this.latitude ||
              isNaN(this.latitude) ||
              this.latitude === '' ||
              !this.longitude ||
              isNaN(this.longitude) ||
              this.longitude === ''
            ) {
              this.geocodingDisabled = false;
            } else {
              this.geocodingDisabled = true;
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );

      lng.valueChanges
        .pipe(distinctUntilChanged(), startWith(lng.value))
        .subscribe(
          (res) => {
            this.longitude = res;

            if (
              !this.latitude ||
              isNaN(this.latitude) ||
              this.latitude === '' ||
              !this.longitude ||
              isNaN(this.longitude) ||
              this.longitude === ''
            ) {
              this.geocodingDisabled = false;
            } else {
              this.geocodingDisabled = true;
            }
          },
          (err) => {
            this.helperService.handleError(err);
          }
        );
    }
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = {
      streetNumber: this.data.streetNumber || '',
      route: this.data.route || '',
      postalCode: this.data.postalCode || '',
      city: this.data.city || '',
      administrativeArea1: this.data.administrativeArea1 || '',
      administrativeArea2: this.data.administrativeArea2 || '',
      administrativeArea3: this.data.administrativeArea3 || '',
      countryCodeTwo: this.data.countryCodeTwo || 'IT', // IT as default value
      latitude: this.data.latitude,
      longitude: this.data.longitude
    };

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: 'row',
        hooks: {
          onInit: (): void => {
            this.geocoding();
          },
        },
        fieldGroup: [
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'route',
            templateOptions: {
              label: this.translateService.instant('common.words.route'),
              placeholder: this.translateService.instant('common.words.route'),
              required: this.required,
              minLength: 1,
              maxLength: 100,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'streetNumber',
            templateOptions: {
              label: this.translateService.instant('common.words.streetNumber'),
              placeholder: this.translateService.instant('common.words.streetNumber'),
              required: this.required,
              minLength: 1,
              maxLength: 100,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'city',
            templateOptions: {
              label: this.translateService.instant('common.words.city'),
              placeholder: this.translateService.instant('common.words.city'),
              required: this.required,
              minLength: 1,
              maxLength: 100,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'postalCode',
            templateOptions: {
              label: this.translateService.instant('common.words.postalCode'),
              placeholder: this.translateService.instant('common.words.postalCode'),
              required: this.required,
              minLength: 1,
              maxLength: 100,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'administrativeArea1',
            templateOptions: {
              label: this.translateService.instant('common.words.administrativeArea1'),
              placeholder: this.translateService.instant('common.words.administrativeArea1'),
              required: false,
              minLength: 1,
              maxLength: 100,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'administrativeArea2',
            templateOptions: {
              label: this.translateService.instant('common.words.administrativeArea2'),
              placeholder: this.translateService.instant('common.words.administrativeArea2'),
              required: false,
              minLength: 1,
              maxLength: 100,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'administrativeArea3',
            templateOptions: {
              label: this.translateService.instant('common.words.administrativeArea3'),
              placeholder: this.translateService.instant('common.words.administrativeArea3'),
              required: false,
              minLength: 1,
              maxLength: 100,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'select',
            key: 'countryCodeTwo',
            templateOptions: {
              label: this.translateService.instant('common.words.country'),
              placeholder: this.translateService.instant('common.words.country'),
              required: this.required,
              minLength: 2,
              maxLength: 2,
              options: this.countries,
            },
            defaultValue: 'IT'
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'latitude',
            templateOptions: {
              label: this.translateService.instant('common.words.latitude'),
              placeholder: this.translateService.instant('common.words.latitude'),
              required: false,
            },
          },
          {
            className: 'col-12 col-md-6',
            type: 'input',
            key: 'longitude',
            templateOptions: {
              label: this.translateService.instant('common.words.longitude'),
              placeholder: this.translateService.instant('common.words.longitude'),
              required: false,
            },
          },
        ],
      },
    ];

    this.dialogIsLoading = false;
  }
}
